/**
 * Which carrier a PMS policy is filed under — the pure half of CarrierResolution.
 *
 * The backend (clinics/services/carrier_matching.py) tells us, per policy, what
 * state its carrier is in:
 *   matched        — we know this PMS carrier (the office's answer, an alias, or
 *                    a near-exact spelling). Saved without asking in auto-fetch.
 *   suggested      — the LLM thinks it's one of ours. Pre-selected; a person
 *                    confirms it by approving.
 *   needs_carrier  — nothing matched: the office adds the carrier (name + phone,
 *                    prefilled from the PMS).
 *   placeholder    — the PMS doesn't name a carrier ("Unknown", a claims network):
 *                    choose one, or add it.
 *   locked         — a person set this policy's carrier by hand; it is kept.
 *
 * A "choice" is what the user settled on:
 *   { kind: "provider", id }                      — one of our carriers
 *   { kind: "create", name, phone, confirmNew }   — add a carrier we don't have
 */
// The backend's placeholder carrier row ("Unknown carrier"). It exists so the
// policy FK could be backfilled; a policy must never be saved under it.
export const UNKNOWN_CARRIER_CODE = "__UNKNOWN__";

export function resolutionState(preview) {
  const carrier = (preview && preview.carrier) || {};
  if (carrier.state) return carrier.state;
  // A backend that predates carrier states.
  if (carrier.locked_carrier_id) return "locked";
  if (carrier.auto_match_recommended) return "matched";
  return "needs_carrier";
}

export function initialChoice(preview) {
  const carrier = (preview && preview.carrier) || {};
  switch (resolutionState(preview)) {
    case "locked":
      return { kind: "provider", id: carrier.locked_carrier_id };
    case "matched":
      return { kind: "provider", id: (carrier.matched && carrier.matched.id) || carrier.auto_match_recommended };
    case "suggested":
      return { kind: "provider", id: carrier.suggested && carrier.suggested.id };
    case "needs_carrier":
      return {
        kind: "create",
        // The name as printed — by the PMS, or on the claim form.
        name: carrier.name_raw || carrier.sikka_carrier_name_raw || "",
        phone: carrier.pms_phone || "",
        confirmNew: false,
      };
    default:
      return null; // placeholder: the user has to choose or add
  }
}

export function phoneDigits(phone) {
  return String(phone || "").replace(/\D/g, "").length;
}

export function isPhoneValid(phone) {
  const text = String(phone || "").trim();
  return phoneDigits(text) >= 10 && text.length <= 32;
}

export function isNameValid(name) {
  const text = String(name || "").trim();
  return text.length > 0 && text.length <= 128;
}

/** Enough to save: a carrier picked, or a new one with a name and a phone. */
export function isChoiceComplete(choice) {
  if (!choice) return false;
  if (choice.kind === "provider") return choice.id !== undefined && choice.id !== null && choice.id !== "";
  if (choice.kind === "create") return isNameValid(choice.name) && isPhoneValid(choice.phone);
  return false;
}

/** The `carrier_choice` the save endpoint expects. */
export function choicePayload(choice) {
  if (choice && choice.kind === "create") {
    return {
      create: { name: String(choice.name || "").trim(), phone: String(choice.phone || "").trim() },
      confirm_new: !!choice.confirmNew,
    };
  }
  return { insurance_provider_id: choice && choice.kind === "provider" ? choice.id : null };
}

/**
 * Carriers offered when the user says "wrong carrier" or "it's one we have":
 * every active real carrier, by name — no match scores. The weak scored
 * suggestions are exactly what people used to pick wrongly.
 */
export function correctionOptions(providers) {
  return (providers || [])
    .filter(function (p) { return p && p.code !== UNKNOWN_CARRIER_CODE && p.is_active !== false; })
    .map(function (p) { return { value: p.id, label: p.name || p.code }; })
    .sort(function (a, b) { return String(a.label).localeCompare(String(b.label)); });
}

/**
 * A 409 from "add this carrier": we already have it (`exists`, not
 * overridable) or it looks like one we have (`maybe`, overridable).
 */
export function parseCarrierConflict(json) {
  if (!json) return null;
  if (json.error_type === "carrier_exists") {
    return { kind: "exists", provider: json.provider || null, message: json.error || "" };
  }
  if (json.error_type === "carrier_maybe_exists") {
    return { kind: "maybe", provider: json.provider || null, reason: json.reason || "", message: json.error || "" };
  }
  return null;
}

/**
 * The wizard's refresh of a patient who already has policies used to drop an
 * unmatched PMS policy on the floor. Keep it whenever the caller owns the review.
 */
export function shouldStagePendingReview(deferReview, outcome) {
  return !!(deferReview && outcome && outcome.preview_token && (outcome.policies || []).length);
}
